import { useCallback } from 'react';
import { LocalParticipant, Room, Track } from 'livekit-client';
import { Participant } from '../types';

interface UseMediaControlsProps {
  localParticipant: LocalParticipant | null;
  room: Room | null;
  onParticipantsUpdate: (participants: Participant[]) => void;
}

export const useMediaControls = ({
  localParticipant,
  room,
  onParticipantsUpdate
}: UseMediaControlsProps) => {

  const toggleVideo = useCallback(async () => {
    if (localParticipant) {
      try {
        await localParticipant.setCameraEnabled(!localParticipant.isCameraEnabled);
        // Update participants list after toggling
        if (room) {
          // This would typically trigger a participants update through room events
          // For now, we'll call the callback directly
          onParticipantsUpdate([]); // This should be replaced with actual participants update
        }
      } catch (error) {
        console.error('Error toggling video:', error);
      }
    }
  }, [localParticipant, room, onParticipantsUpdate]);

  const toggleAudio = useCallback(async () => {
    if (localParticipant) {
      try {
        await localParticipant.setMicrophoneEnabled(!localParticipant.isMicrophoneEnabled);
        // Update participants list after toggling
        if (room) {
          onParticipantsUpdate([]); // This should be replaced with actual participants update
        }
      } catch (error) {
        console.error('Error toggling audio:', error);
      }
    }
  }, [localParticipant, room, onParticipantsUpdate]);


  const switchCamera = useCallback(async (deviceId: string) => {
    if (!localParticipant || !room) {
      console.warn('Cannot switch camera: no local participant or room');
      return;
    }

    try {
      // Get video track with new device
      const videoTrack = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: deviceId } },
        audio: false,
      });

      // Stop current video track
      const videoPublications = Array.from(localParticipant.videoTrackPublications.values());
      for (const publication of videoPublications) {
        if (publication.source === Track.Source.Camera && publication.track) {
          publication.track.stop();
          await localParticipant.unpublishTrack(publication.track);
        }
      }

      // Publish new video track
      await localParticipant.publishTrack(videoTrack.getVideoTracks()[0], {
        source: Track.Source.Camera,
      });

      // Update participants list
      if (room) {
        onParticipantsUpdate([]); // This should be replaced with actual participants update
      }
    } catch (error) {
      console.error('Error switching camera:', error);
      throw error;
    }
  }, [localParticipant, room, onParticipantsUpdate]);

  const switchMicrophone = useCallback(async (deviceId: string) => {
    if (!localParticipant || !room) {
      console.warn('Cannot switch microphone: no local participant or room');
      return;
    }

    try {
      // Get audio track with new device
      const audioTrack = await navigator.mediaDevices.getUserMedia({
        video: false,
        audio: { deviceId: { exact: deviceId } },
      });

      // Stop current audio track
      const audioPublications = Array.from(localParticipant.audioTrackPublications.values());
      for (const publication of audioPublications) {
        if (publication.source === Track.Source.Microphone && publication.track) {
          publication.track.stop();
          await localParticipant.unpublishTrack(publication.track);
        }
      }

      // Publish new audio track
      await localParticipant.publishTrack(audioTrack.getAudioTracks()[0], {
        source: Track.Source.Microphone,
      });

      // Update participants list
      if (room) {
        onParticipantsUpdate([]); // This should be replaced with actual participants update
      }
    } catch (error) {
      console.error('Error switching microphone:', error);
      throw error;
    }
  }, [localParticipant, room, onParticipantsUpdate]);

  return {
    toggleVideo,
    toggleAudio,
    switchCamera,
    switchMicrophone,
  };
};
